import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { itineraryApi } from '../api/itinerary.api';
import { dismissToast, toast } from '../components/ui/Toast';
import type { HistoryEntry } from '../utils/itineraryHistoryEntries';
import type { ItineraryDay } from '../types';

type SetDays = Dispatch<SetStateAction<ItineraryDay[]>>;

/** How long a deferred delete waits, and how long its toast offers to undo it. */
export const UNDO_WINDOW_MS = 8000;
const MAX_STEPS = 3;

export interface ItineraryHistory {
  /**
   * `setDays` for the actions that go through `run`. Every other writer keeps
   * the plain one, and that is what lets the history notice them.
   */
  setDays: SetDays;
  /** The plan as it is right now, for entries that diff against it. */
  getDays: () => ItineraryDay[];
  /** Runs an action and records the entry it returns (null = nothing to undo). */
  run: (action: () => Promise<HistoryEntry | null>) => Promise<void>;
  /** With `only`, undoes it only if it is still the latest step — a stale toast's button. */
  undo: (only?: HistoryEntry) => Promise<void>;
  redo: () => Promise<void>;
  /** Empties the history, for a tracked write that is not a step (moving a booking card). */
  forget: () => void;
  canUndo: boolean;
  canRedo: boolean;
  busy: boolean;
}

/**
 * What the recorded inverses depend on: which stops each day holds, in which
 * order, and whether it continues from the day before. Hours and titles change
 * under the history without making any inverse wrong.
 */
export function planFingerprint(days: ItineraryDay[]): string {
  return days
    .map(d => `${d.id}:${d.startFromPrevStop ? 1 : 0}:${d.activities.filter(a => !a.isOrigin).map(a => a.id).join(',')}`)
    .join('|');
}

/**
 * Undo/redo for the itinerary, shared by the Itinerary tab and the map's routes
 * (ADR-0015). Three steps, per trip, in memory.
 *
 * - A deferred write (a delete) is sent when its window closes or the next
 *   action starts, and the moment it is sent it becomes a barrier: it and every
 *   step before it leave the history.
 * - A write to `days` that did not come through `setDays` here empties the
 *   history. It is detected by fingerprint rather than by asking every writer to
 *   call `clear()`, because the next writer added would not.
 */
export function useItineraryHistory(tripId: string, days: ItineraryDay[], setDays: SetDays): ItineraryHistory {
  const { t } = useTranslation();
  const undoStack = useRef<HistoryEntry[]>([]);
  const redoStack = useRef<HistoryEntry[]>([]);
  const pending = useRef<{ entry: HistoryEntry; timer: ReturnType<typeof setTimeout> } | null>(null);
  const toastId = useRef<number | null>(null);
  const expected = useRef(planFingerprint(days));
  const daysRef = useRef(days);
  daysRef.current = days;
  const busyRef = useRef(false);
  const [busy, setBusyState] = useState(false);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const setBusy = (value: boolean) => { busyRef.current = value; setBusyState(value); };

  const trackedSetDays = useCallback<SetDays>(update => {
    setDays(prev => {
      const next = typeof update === 'function' ? update(prev) : update;
      expected.current = planFingerprint(next);
      return next;
    });
  }, [setDays]);

  const getDays = useCallback(() => daysRef.current, []);

  const dismissUndoToast = useCallback(() => {
    if (toastId.current !== null) dismissToast(toastId.current);
    toastId.current = null;
  }, []);

  const reload = useCallback(async () => {
    try { trackedSetDays(await itineraryApi.listDays(tripId)); } catch { /* the next load will fix it */ }
  }, [tripId, trackedSetDays]);

  // Sends the pending deferred write. From here on it cannot be undone, so it
  // and everything below it leave the stack.
  const flush = useCallback(async (reloadOnFailure = true) => {
    const p = pending.current;
    if (!p?.entry.commit) return;
    clearTimeout(p.timer);
    pending.current = null;
    const at = undoStack.current.indexOf(p.entry);
    if (at >= 0) undoStack.current = undoStack.current.slice(at + 1);
    rerender();
    try {
      await p.entry.commit();
    } catch {
      toast(t('itinerary.toast.failed'), 'error');
      if (reloadOnFailure) await reload();
    }
  }, [t, reload]);

  const schedule = useCallback((entry: HistoryEntry) => {
    if (!entry.commit) return;
    pending.current = { entry, timer: setTimeout(() => { void flush(); }, UNDO_WINDOW_MS) };
  }, [flush]);

  const clear = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    dismissUndoToast();
    rerender();
  }, [dismissUndoToast]);

  const push = (entry: HistoryEntry) => {
    undoStack.current = [...undoStack.current, entry].slice(-MAX_STEPS);
  };

  const undo = useCallback(async (only?: HistoryEntry) => {
    const entry = undoStack.current.at(-1);
    if (!entry || busyRef.current || (only && only !== entry)) return;
    setBusy(true);
    if (pending.current?.entry === entry) {
      clearTimeout(pending.current.timer);
      pending.current = null;
    }
    undoStack.current = undoStack.current.slice(0, -1);
    dismissUndoToast();
    try {
      await entry.undo();
      redoStack.current = [...redoStack.current, entry];
      toast(t('itinerary.undo.undone'), 'info');
    } catch {
      toast(t('itinerary.undo.failed'), 'error');
      clear();
      await reload();
    } finally {
      setBusy(false);
      rerender();
    }
  }, [t, clear, reload, dismissUndoToast]);

  const showUndoToast = useCallback((entry: HistoryEntry) => {
    dismissUndoToast();
    toastId.current = toast(entry.message, 'success', {
      durationMs: UNDO_WINDOW_MS,
      action: { label: t('itinerary.undo.action'), onClick: () => { void undo(entry); } },
    });
  }, [t, undo, dismissUndoToast]);

  const redo = useCallback(async () => {
    const entry = redoStack.current.at(-1);
    if (!entry || busyRef.current) return;
    setBusy(true);
    await flush();
    redoStack.current = redoStack.current.slice(0, -1);
    try {
      await entry.redo();
      push(entry);
      schedule(entry);
      showUndoToast(entry);
    } catch {
      toast(t('itinerary.undo.failed'), 'error');
      clear();
      await reload();
    } finally {
      setBusy(false);
      rerender();
    }
  }, [t, flush, schedule, showUndoToast, clear, reload]);

  const run = useCallback(async (action: () => Promise<HistoryEntry | null>) => {
    if (busyRef.current) return;
    setBusy(true);
    try {
      // The action must start from what the server holds, so a delete still in
      // its window goes out first — and becomes the barrier.
      await flush();
      const entry = await action();
      if (!entry) return;
      push(entry);
      redoStack.current = [];
      schedule(entry);
      showUndoToast(entry);
    } finally {
      setBusy(false);
      rerender();
    }
  }, [flush, schedule, showUndoToast]);

  // A write that did not come through `trackedSetDays`: the plan is no longer
  // the one the inverses were built for.
  useEffect(() => {
    const fingerprint = planFingerprint(days);
    if (fingerprint === expected.current) return;
    expected.current = fingerprint;
    const p = pending.current;
    if (p) {
      // A refetch brings the cards of a delete still in its window back: hide
      // them again, then send it.
      void p.entry.redo().then(() => flush());
    }
    if (undoStack.current.length > 0 || redoStack.current.length > 0) clear();
  }, [days, flush, clear]);

  // Leaving the trip (or the page) sends what is pending; the history does not
  // survive it. Through a ref, so a new `flush` identity is not "leaving".
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    const onPageHide = () => { void flushRef.current(false); };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      void flushRef.current(false);
      undoStack.current = [];
      redoStack.current = [];
    };
  }, [tripId]);

  return {
    setDays: trackedSetDays,
    getDays,
    run,
    undo,
    redo,
    forget: clear,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    busy,
  };
}
